import { useEffect, useState } from 'react';

import {
  type Campaign,
  type ContentAddress,
  fetchBrandBookProfile,
  fetchBrandBooks,
  fetchCampaigns,
  isDutchLanguage,
  languageLabel,
  type Repository,
} from './api.ts';
import * as wording from './wording.ts';

/**
 * The pieces of a form that says where a content comes from: which
 * repositories it reads, and which language it speaks. Shared by the New
 * content form and the new-batch form (US-122), so a batch's ideas are pointed
 * at their product the same way a single content is.
 */

/**
 * The languages offered when the server's list has not arrived. The server
 * sends its own with the content list, and accepts any other language tag.
 */
export const FALLBACK_LANGUAGES = ['nl-NL', 'nl-BE', 'en-US', 'en-GB', 'de-DE', 'fr-FR'] as const;

export interface ProfileLanguage {
  readonly language: string;
  readonly address: ContentAddress | '';
  /** The operator's own pick: from here on a change of repository leaves it alone. */
  readonly chooseLanguage: (language: string) => void;
  readonly chooseAddress: (address: ContentAddress | '') => void;
}

/**
 * The language follows the product (synthesis C2): the profile on the primary
 * repository's own brand book names it, and Settings → Defaults answers when it does not. Only
 * until the operator picks one — a choice made on the page is never
 * overwritten.
 */
export function useProfileLanguage(
  primary: string | null,
  defaultLanguage: string | null,
): ProfileLanguage {
  const [language, setLanguage] = useState('en-US');
  const [address, setAddress] = useState<ContentAddress | ''>('');
  const [chosen, setChosen] = useState(false);

  useEffect(() => {
    if (chosen) return;
    const fallback = defaultLanguage ?? 'en-US';
    if (primary === null) {
      setLanguage(fallback);
      setAddress('');
      return;
    }
    const controller = new AbortController();
    fetchBrandBooks(controller.signal)
      .then((books) => {
        // The book extracted from this repository, as the server reads it.
        const book = books.find(
          (one) => one.sourceType === 'repository' && one.repositoryId === primary,
        );
        return book === undefined ? null : fetchBrandBookProfile(book.id, controller.signal);
      })
      .then((view) => {
        // An answer that arrived just as the operator picked a language (or
        // another repository) was already queued when the abort came.
        if (controller.signal.aborted) return;
        setLanguage(view?.profile.language ?? fallback);
        setAddress(view?.profile.address ?? '');
      })
      .catch(() => {
        if (!controller.signal.aborted) setLanguage(fallback);
      });
    return () => {
      controller.abort();
    };
  }, [primary, defaultLanguage, chosen]);

  return {
    language,
    address,
    chooseLanguage: (next) => {
      setLanguage(next);
      setChosen(true);
    },
    chooseAddress: (next) => {
      setAddress(next);
      setChosen(true);
    },
  };
}

/** The language select and, for Dutch, the form of address beside it. */
export function LanguageField({
  id,
  choice,
  languages,
  hint,
}: {
  readonly id: string;
  readonly choice: ProfileLanguage;
  readonly languages: readonly string[];
  readonly hint: string;
}) {
  const { language, address } = choice;
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {wording.LANGUAGE}
      </label>
      <select
        id={id}
        className="field__input"
        value={language}
        onChange={(event) => {
          choice.chooseLanguage(event.target.value);
        }}
        aria-describedby={`${id}-hint`}
      >
        {(languages.includes(language) ? languages : [language, ...languages]).map((option) => (
          <option key={option} value={option}>
            {languageLabel(option)}
          </option>
        ))}
      </select>
      {isDutchLanguage(language) && (
        <div className="field__actions" role="radiogroup" aria-label={wording.ADDRESS_LABEL}>
          {wording.ADDRESS_CHOICES.map(([value, label]) => (
            <label className="radio" key={value === '' ? 'none' : value}>
              <input
                type="radio"
                name={`${id}-address`}
                value={value}
                checked={address === value}
                onChange={() => {
                  choice.chooseAddress(value);
                }}
              />
              {label}
            </label>
          ))}
        </div>
      )}
      <p className="field__hint" id={`${id}-hint`}>
        {hint}
        {isDutchLanguage(language) && ` ${wording.ADDRESS_HINT}`}
      </p>
    </div>
  );
}

/** One repository, with its place in the order that decides the primary. */
export function RepositoryChoice({
  repository,
  position,
  onToggle,
  onMakePrimary,
}: {
  readonly repository: Repository;
  /** Its index among the chosen repositories, or -1 when it is not chosen. */
  readonly position: number;
  readonly onToggle: () => void;
  readonly onMakePrimary: () => void;
}) {
  const chosen = position >= 0;
  return (
    <li className={`choice${chosen ? ' choice--chosen' : ''}`}>
      <label className="choice__label">
        <input type="checkbox" checked={chosen} onChange={onToggle} />
        <span className="choice__main">
          <span className="choice__title">
            {repository.name}
            {position === 0 && <span className="choice__tag">{wording.PRIMARY_TAG}</span>}
          </span>
          <span className="choice__summary mono">{repository.githubSlug ?? repository.sshUrl}</span>
        </span>
      </label>
      {position > 0 && (
        <button type="button" className="button button--small button--quiet" onClick={onMakePrimary}>
          {wording.MAKE_PRIMARY}
        </button>
      )}
    </li>
  );
}

/**
 * Picking and unpicking repositories, in the order they were picked: the
 * first is the primary one, and moving one to the front is what makes it so.
 */
export function useRepositoryOrder(): {
  readonly chosen: readonly string[];
  readonly toggle: (id: string) => void;
  readonly makePrimary: (id: string) => void;
} {
  const [chosen, setChosen] = useState<string[]>([]);
  return {
    chosen,
    toggle: (id) => {
      setChosen((current) =>
        current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
      );
    },
    makePrimary: (id) => {
      setChosen((current) => [id, ...current.filter((entry) => entry !== id)]);
    },
  };
}

export interface CampaignChoice {
  /** The active campaigns; empty until they are read, or when they cannot be. */
  readonly campaigns: readonly Campaign[];
  /** The chosen campaign's id; `''` for none. */
  readonly campaignId: string;
  readonly chooseCampaign: (id: string) => void;
}

/**
 * The campaign new work starts in (US-030): the active campaigns, read once,
 * with the address's `?campaign=` picked when it names one of them. An
 * archived campaign is never offered, since the server refuses to create work
 * in one. A failed read leaves the choice empty rather than blocking the form.
 */
export function useCampaignChoice(): CampaignChoice {
  const [campaigns, setCampaigns] = useState<readonly Campaign[]>([]);
  const [campaignId, setCampaignId] = useState('');
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get('campaign');
    const controller = new AbortController();
    fetchCampaigns(controller.signal)
      .then((all) => {
        const active = all.filter((campaign) => campaign.archivedAt === null);
        setCampaigns(active);
        if (wanted !== null && active.some((campaign) => campaign.id === wanted)) {
          setCampaignId((current) => (current === '' ? wanted : current));
        }
      })
      .catch(() => undefined);
    return () => {
      controller.abort();
    };
  }, []);
  return { campaigns, campaignId, chooseCampaign: setCampaignId };
}

/** The optional **Campaign** select of the New content and new-batch forms. */
export function CampaignField({
  id,
  choice,
  hint,
}: {
  readonly id: string;
  readonly choice: CampaignChoice;
  readonly hint: string;
}) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {wording.CARD_FIELD_CAMPAIGN}
      </label>
      <select
        id={id}
        className="field__input"
        value={choice.campaignId}
        onChange={(event) => {
          choice.chooseCampaign(event.target.value);
        }}
        aria-describedby={`${id}-hint`}
      >
        <option value="">{wording.CARD_FIELD_NO_CAMPAIGN}</option>
        {choice.campaigns.map((campaign) => (
          <option key={campaign.id} value={campaign.id}>
            {campaign.name}
          </option>
        ))}
      </select>
      <p className="field__hint" id={`${id}-hint`}>
        {hint}
      </p>
    </div>
  );
}
